import React, { useRef, useState } from 'react';
import Markdown from 'react-markdown';
import useReorderDragDrop from 'hooks/useReorderDragDrop';

import {
  FAQRow,
  FAQItemTitle,
  FAQItemContent,
  H2,
  H3,
  H4,
  DragHandlerWrapper,
  EditButton,
  ButtonsWrapper,
  TextArea,
  Input,
  LabelArea,
  EditContentButtonWrapper,
  PrimaryButton,
  CancelButton,
  DeleteButton,
} from './shared/FAQ.styles';
import LinkRenderer from 'components/utility/LinkRenderer/LinkRenderer';
import { StyledCaret } from 'components/Charts/ChartTable/chartTable.style';
import { ReactComponent as DragIcon } from 'icons/icon-drag.svg';

import { IFAQItem, allowedMarkdownTypes } from './shared/shared';
import { DragHandler } from 'components/Selector/DSPSelector/SelectedDSPList/DSPItem/DSPItem';
import { confirmAlert } from 'components/base/ConfirmAlert';

interface IFAQItemProps {
  faqItem: IFAQItem;
  index: number;
  isActive: boolean;
  toggleExpand: () => void;
  changeOrder: (start: number, end: number) => void;
  isAdminRole: boolean;
  afterDrop: () => void;
  onEditItem: (item: IFAQItem) => void;
  onDeleteItem: () => void;
}

const LinkRendererComponent: React.FC<any> = (props: any) => (
  <LinkRenderer {...props} />
);

const HeadingRenderer: React.FC<any> = (props) => {
  const { level, children } = props;

  switch (level) {
    case 1: {
      return <H2>{children}</H2>;
    }
    case 2: {
      return <H3>{children}</H3>;
    }
    default: {
      return <H4>{children}</H4>;
    }
  }
};

const FAQItem: React.FC<IFAQItemProps> = (props) => {
  const {
    faqItem,
    index,
    isActive,
    toggleExpand,
    changeOrder,
    isAdminRole,
    afterDrop,
    onEditItem,
    onDeleteItem,
  } = props;
  const ref = useRef(null);
  const { isDragging, drag, preview } = useReorderDragDrop(
    ref,
    faqItem,
    index,
    changeOrder,
    afterDrop
  );

  const [editMode, setEditMode] = useState(false);
  const opacity = isDragging ? 0 : 1;

  const textAreaTitle = useRef(null);
  const textArea = useRef(null);

  const cancelEvents = (e: any): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onEditClick = (e: any): void => {
    cancelEvents(e);
    if (!isActive) toggleExpand();
    setEditMode(!editMode);
  };

  const saveMessage = (): void => {
    if (isAdminRole && textArea && textAreaTitle) {
      const newItemValue = {
        title: (textAreaTitle as any).current?.value,
        content: (textArea as any).current?.value,
      };

      onEditItem(newItemValue);
      setEditMode(false);
    }
  };

  const deleteItem = (): void => {
    if (isAdminRole) {
      confirmAlert({
        title: 'Confirm to Delete',
        message: 'Are you sure you want to delete this section?',
        onConfirm: onDeleteItem,
      });
    }
  };

  const toggle = (): void => {
    if (editMode && !isActive) setEditMode(false);
    toggleExpand();
  };

  return (
    <FAQRow
      key={faqItem.title}
      style={{ opacity }}
      isDragging={isDragging}
      ref={preview}
    >
      <FAQItemTitle activeTitle={isActive} onClick={toggle}>
        {faqItem.title}
        <ButtonsWrapper
          ref={ref}
          onMouseDown={(e: any): void => e.preventDefault()}
        >
          {isAdminRole && <EditButton onClick={onEditClick}>Edit</EditButton>}
          <StyledCaret open={isActive} />
          {isAdminRole && (
            <DragHandlerWrapper>
              <DragHandler
                ref={drag}
                onClick={(e: any): any => cancelEvents(e)}
                onMouseDown={(e: any): any => e.stopPropagation()}
              >
                <DragIcon />
              </DragHandler>
            </DragHandlerWrapper>
          )}
        </ButtonsWrapper>
      </FAQItemTitle>
      {isActive && (
        <FAQItemContent>
          {!editMode && (
            <Markdown
              source={faqItem.content}
              allowedTypes={allowedMarkdownTypes}
              renderers={{
                link: LinkRendererComponent,
                heading: HeadingRenderer,
              }}
            />
          )}
          {editMode && (
            <>
              <LabelArea>Title</LabelArea>
              <Input
                type="text"
                defaultValue={faqItem.title}
                ref={textAreaTitle}
                onMouseDown={(e: any): void => e.stopPropagation()}
                onDragStart={cancelEvents}
                draggable
              />
              <LabelArea>Content</LabelArea>
              <TextArea
                rows="10"
                defaultValue={faqItem.content}
                ref={textArea}
                onMouseDown={(e: any): void => e.stopPropagation()}
                onDragStart={cancelEvents}
                draggable
              />
              {editMode && (
                <EditContentButtonWrapper>
                  <>
                    <PrimaryButton onClick={saveMessage}>Publish</PrimaryButton>
                    <CancelButton onClick={(): void => setEditMode(false)}>
                      Cancel
                    </CancelButton>
                    <DeleteButton onClick={deleteItem}>Delete</DeleteButton>
                  </>
                </EditContentButtonWrapper>
              )}
            </>
          )}
        </FAQItemContent>
      )}
    </FAQRow>
  );
};

export default FAQItem;
